// Package 4, part L (owner, 7. 10. 2026; "Zvuk i radio"): the sound system on Howler.js. Four channels with their own
// volume (effects, music, ambience, radio) and a mute for all of them (the header's button); effects on by default, music
// and ambience quiet. Nothing plays before the player's first touch (browsers want one), and everything goes quiet while
// the tab is in the background. Effects are made when the game starts, loops the first time a screen asks for them.
// The settings stay in this browser (`localStorage`).

import { useEffect, useSyncExternalStore } from 'react';
import { Howl, Howler } from 'howler';
import { renderEffect, renderLoop, wavUrl, type EffectId, type LoopId } from './synth';

export type Channel = 'effects' | 'music' | 'ambient' | 'radio';

export interface SoundSettings {
  muted: boolean;
  effects: number;
  music: number;
  ambient: number;
  radio: number;
}

/** The defaults: effects on, music and ambience quiet, the radio in between. */
export const SOUND_DEFAULTS: SoundSettings = { muted: false, effects: 0.8, music: 0.2, ambient: 0.2, radio: 0.6 };

const KEY = 'ld_sound';

function load(): SoundSettings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { ...SOUND_DEFAULTS, ...(JSON.parse(raw) as Partial<SoundSettings>) };
  } catch {
    // Private windows and blocked storage: the defaults.
  }
  return { ...SOUND_DEFAULTS };
}

let settings: SoundSettings = load();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

export function soundSettings(): SoundSettings {
  return settings;
}

export function setSoundSettings(next: Partial<SoundSettings>) {
  settings = { ...settings, ...next };
  try {
    localStorage.setItem(KEY, JSON.stringify(settings));
  } catch {
    // Not kept: fine for this visit.
  }
  applyVolumes();
  emit();
}

export function useSoundSettings(): SoundSettings {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => settings,
    () => settings,
  );
}

// ---- The player's first touch, the tab in the background ----------------------------------------------------------

let unlocked = false;
let hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

function applyMute() {
  Howler.mute(settings.muted || hidden);
}

if (typeof window !== 'undefined') {
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    // The loops a screen asked for before the touch start now.
    for (const l of wanted.values()) startLoop(l.id, l.channel);
  };
  window.addEventListener('pointerdown', unlock);
  window.addEventListener('keydown', unlock);
  document.addEventListener('visibilitychange', () => {
    hidden = document.visibilityState === 'hidden';
    applyMute();
  });
}

// ---- The real files (docs/AUDIO_LICENSES.md) ------------------------------------------------------------------------

/**
 * Sounds that have a real file in `public/assets/audio/` (`<id>.ogg`, and `<id>.m4a` for Safari); the rest are made in
 * code (synth.ts). A file is added here when it is in the folder.
 */
export const AUDIO_FILES: ReadonlySet<EffectId | LoopId> = new Set<EffectId | LoopId>([]);

const srcOf = (id: EffectId | LoopId, make: () => Float32Array): { src: string[]; format: string[] } =>
  AUDIO_FILES.has(id) ? { src: [`/assets/audio/${id}.ogg`, `/assets/audio/${id}.m4a`], format: ['ogg', 'm4a'] } : { src: [wavUrl(make())], format: ['wav'] };

// ---- Effects --------------------------------------------------------------------------------------------------------

const effects = new Map<EffectId, Howl>();

function effect(id: EffectId): Howl {
  let h = effects.get(id);
  if (!h) {
    h = new Howl({ ...srcOf(id, () => renderEffect(id)), volume: settings.effects });
    effects.set(id, h);
  }
  return h;
}

/** Plays an effect (on the effects channel; the radio's jingle on the radio's). Nothing before the first touch. */
export function sfx(id: EffectId) {
  if (!unlocked || settings.muted) return;
  try {
    const h = effect(id);
    h.volume(id === 'radio_jingle' ? settings.radio : settings.effects);
    h.play();
  } catch {
    // No audio here (an old browser, a test): silent.
  }
}

/** The effects the game plays most, made while it starts (the rest when first played). */
export function preloadEffects() {
  if (typeof window === 'undefined') return;
  const ids: EffectId[] = ['report_open', 'loot_common', 'loot_uncommon', 'loot_rare', 'level_up', 'goal_claimed'];
  const go = () => ids.forEach((id) => effect(id));
  const idle = (window as unknown as { requestIdleCallback?: (f: () => void) => void }).requestIdleCallback;
  if (idle) idle(go);
  else setTimeout(go, 500);
}

// ---- Loops: ambience, music, the radio's static ----------------------------------------------------------------------

const loops = new Map<LoopId, Howl>();
/** What the screens on now want playing (started on the first touch). */
const wanted = new Map<string, { id: LoopId; channel: Channel }>();

function loopVolume(channel: Channel) {
  return channel === 'music' ? settings.music : channel === 'ambient' ? settings.ambient : settings.radio;
}

function startLoop(id: LoopId, channel: Channel) {
  if (!unlocked) return;
  try {
    let h = loops.get(id);
    if (!h) {
      h = new Howl({ ...srcOf(id, () => renderLoop(id)), loop: true, volume: 0 });
      loops.set(id, h);
    }
    if (!h.playing()) h.play();
    h.fade(h.volume(), loopVolume(channel), 800);
  } catch {
    // No audio: silent.
  }
}

function stopLoop(id: LoopId) {
  const h = loops.get(id);
  if (!h || !h.playing()) return;
  h.fade(h.volume(), 0, 600);
  setTimeout(() => {
    if (![...wanted.values()].some((w) => w.id === id)) h.pause();
  }, 650);
}

function applyVolumes() {
  applyMute();
  for (const w of wanted.values()) loops.get(w.id)?.volume(loopVolume(w.channel));
}

/** A screen's ambience and music while it is on (Camp, Map, the horde, the season's end). */
export function useScreenSound(sounds: { ambient?: LoopId | null; music?: LoopId | null }) {
  const { ambient, music } = sounds;
  useEffect(() => {
    const keys: string[] = [];
    const on = (id: LoopId | null | undefined, channel: Channel) => {
      if (!id) return;
      const key = `${channel}:${id}:${Math.random()}`;
      keys.push(key);
      wanted.set(key, { id, channel });
      startLoop(id, channel);
    };
    on(ambient, 'ambient');
    on(music, 'music');
    return () => {
      for (const k of keys) {
        const w = wanted.get(k);
        wanted.delete(k);
        if (w) stopLoop(w.id);
      }
    };
  }, [ambient, music]);
}

/** The radio's static while a broadcast is open (with the jingle first). */
export function useRadioStatic(on: boolean) {
  useEffect(() => {
    if (!on) return;
    sfx('radio_jingle');
    const key = `radio:radio_static:${Math.random()}`;
    const t = setTimeout(() => {
      wanted.set(key, { id: 'radio_static', channel: 'radio' });
      startLoop('radio_static', 'radio');
    }, 1200);
    return () => {
      clearTimeout(t);
      wanted.delete(key);
      stopLoop('radio_static');
    };
  }, [on]);
}

applyMute();
